import { useMemo, type CSSProperties, type ReactElement } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { RfqVendorInvitationRow } from '../../domain/types';

function StaticVendorChip({ rank, label }: { rank: number; label: string }): ReactElement {
  return (
    <div className="rfq-vendor-order-chip rfq-vendor-order-chip--readonly">
      <span className="rfq-vendor-order-rank" aria-hidden>
        {rank}
      </span>
      <span className="rfq-vendor-order-name">{label}</span>
    </div>
  );
}

function SortableVendorChip({ id, rank, label }: { id: string; rank: number; label: string }): ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.92 : 1,
    cursor: 'grab'
  };

  return (
    <div ref={setNodeRef} style={style} className="rfq-vendor-order-chip">
      <span className="rfq-vendor-order-rank" aria-hidden>
        {rank}
      </span>
      <span className="rfq-vendor-order-name">{label}</span>
      <button
        type="button"
        className="rfq-vendor-order-drag"
        aria-label={`Reorder ${label}, drag to change recommendation rank`}
        {...attributes}
        {...listeners}
      >
        ⋮⋮
      </button>
    </div>
  );
}

export interface RfqVendorColumnOrderStripProps {
  orderedInvited: RfqVendorInvitationRow[];
  orderedIds: string[];
  onReorder: (nextIds: string[]) => void;
  readOnly?: boolean;
}

export function RfqVendorColumnOrderStrip({
  orderedInvited,
  orderedIds,
  onReorder,
  readOnly = false
}: RfqVendorColumnOrderStripProps): ReactElement {
  const items = useMemo(() => orderedIds.filter((id) => orderedInvited.some((v) => v.vendorId === id)), [
    orderedIds,
    orderedInvited
  ]);

  const byId = useMemo(() => {
    const m = new Map<string, RfqVendorInvitationRow>();
    for (const v of orderedInvited) {
      m.set(v.vendorId, v);
    }
    return m;
  }, [orderedInvited]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handleDragEnd(event: DragEndEvent): void {
    if (readOnly) {
      return;
    }
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = items.indexOf(String(active.id));
    const newIndex = items.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }
    onReorder(arrayMove(items, oldIndex, newIndex));
  }

  if (readOnly) {
    return (
      <div className="rfq-vendor-order-strip">
        <p className="rfq-vendor-order-strip-heading">
          <strong>Recommendation rank</strong> (left = preferred)
        </p>
        <div className="rfq-vendor-order-strip-inner" role="list" aria-label="Vendor column recommendation order">
          {items.map((id, index) => {
            const v = byId.get(id);
            if (!v) {
              return null;
            }
            return <StaticVendorChip key={id} rank={index + 1} label={v.vendorName} />;
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="rfq-vendor-order-strip">
      <p className="rfq-vendor-order-strip-heading">
        <strong>Recommendation rank</strong> — drag vendors left to right to set preferred order. Default is{' '}
        <strong>quote received</strong> order (earliest first).
      </p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items} strategy={horizontalListSortingStrategy}>
          <div className="rfq-vendor-order-strip-inner" role="list" aria-label="Vendor column recommendation order">
            {items.map((id, index) => {
              const v = byId.get(id);
              if (!v) {
                return null;
              }
              return <SortableVendorChip key={id} id={id} rank={index + 1} label={v.vendorName} />;
            })}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
